import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Card, CardContent, CardMedia, Button, CircularProgress, Stack, Alert, Chip, Grid } from '@mui/material';
import AdminSidebar from './components/AdminSidebar';
import { adminApi, FlaggedPhoto } from '../../api/adminApi';

const EXTRA_NO_FACE_INFO = {
    label: 'Extra photo, no clear face',
    color: 'info' as const,
    help: 'Not the main photo. The member has not been told. Remove takes it off and emails them that every photo needs to show their face; Keep it if it is plainly them.',
};

const STATUS_INFO: Record<FlaggedPhoto['status'], { label: string; color: 'error' | 'warning' | 'info'; help: string }> = {
    urgent: {
        label: 'Urgent: possible minor + explicit',
        color: 'error',
        help: 'Already taken off the profile. Do not download or forward it. Report the photo URL to the IWF (report.iwf.org.uk), suspend the account in User Management, then Remove.',
    },
    child: { label: 'Child', color: 'error', help: 'A face is estimated as a child. Photos of children are not allowed, including their own.' },
    explicit: { label: 'Explicit', color: 'warning', help: 'Nudity, sexual or violent content.' },
    review: { label: 'May be under 18', color: 'info', help: 'A face may be under 18. Keep it if everyone in it is clearly an adult.' },
    no_face: { label: 'Main photo, no clear face', color: 'info', help: 'The photo people see first has no clear face. The member has been emailed once. Keep it if it is plainly them; Remove it if it is scenery, a car or someone else.' },
    small_face: { label: 'Main photo, small face', color: 'info', help: 'A face was found but it is small or unclear (a distance shot, a crowd, a poster). The member has not been told and is still shown in picks. Keep it if it is plainly them; Remove it if you cannot tell.' },
};

const EXTRA_SMALL_FACE_INFO = {
    label: 'Extra photo, small face',
    color: 'info' as const,
    help: 'A face was found but it is small or unclear. The member has not been told. Keep it if it is plainly them; Remove it if you cannot tell.',
};

/**
 * Profile photo review queue: photos the background scan flagged for children
 * or explicit content (see web/api/cron/moderate-photos.js). Keep leaves the
 * photo on the profile; Remove takes it off, deletes the file and emails the
 * member why.
 */
const AdminPhotosScreen: React.FC = () => {
    const [items, setItems] = useState<FlaggedPhoto[]>([]);
    const [checked, setChecked] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actingOn, setActingOn] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminApi.getPhotoQueue();
            setItems(res.items || []);
            setChecked(res.checked || 0);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load flagged photos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const decide = async (photoUrl: string, action: 'keep' | 'remove') => {
        setActingOn(photoUrl);
        setError(null);
        try {
            await adminApi.moderatePhoto(photoUrl, action);
            setItems(prev => prev.filter(i => i.photoUrl !== photoUrl));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Action failed');
        } finally {
            setActingOn(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AdminSidebar />
            <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
                <Typography variant="h4" gutterBottom>Photo Review</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {checked.toLocaleString()} photos checked so far. Flagged photos stay on the profile until you decide, except urgent ones. If removing a photo leaves fewer than 3, the profile is hidden from Daily Picks until the member adds one.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <CircularProgress />
                ) : items.length === 0 ? (
                    <Typography color="text.secondary">No photos waiting for review.</Typography>
                ) : (
                    <Grid container spacing={2}>
                        {items.map(item => {
                            const info = item.isMain === false && item.status === 'no_face' ? EXTRA_NO_FACE_INFO
                                : item.isMain === false && item.status === 'small_face' ? EXTRA_SMALL_FACE_INFO
                                : STATUS_INFO[item.status];
                            const urgent = item.status === 'urgent';
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={item.photoUrl}>
                                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {urgent ? (
                                            <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#2b0008', color: '#fff', p: 2 }}>
                                                <Typography variant="body2" align="center">
                                                    Hidden. Report this URL to the IWF:
                                                    <Box component="span" sx={{ display: 'block', mt: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 11 }}>
                                                        {item.photoUrl}
                                                    </Box>
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <CardMedia component="img" image={item.photoUrl} alt="" sx={{ height: 280, objectFit: 'contain', bgcolor: '#f4f0fb' }} />
                                        )}
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Chip label={info.label} color={info.color} size="small" sx={{ mb: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.name || item.userId}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {item.userId}
                                            </Typography>
                                            {item.ages.length > 0 && (
                                                <Typography variant="body2" sx={{ mt: 1 }}>Estimated ages: {item.ages.join(', ')}</Typography>
                                            )}
                                            {item.labels.length > 0 && (
                                                <Typography variant="body2">
                                                    Labels: {item.labels.map(l => `${l.name} ${l.confidence}%`).join(', ')}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                {info.help}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                {!urgent && (
                                                    <Button
                                                        variant="outlined"
                                                        color="success"
                                                        size="small"
                                                        disabled={actingOn === item.photoUrl}
                                                        onClick={() => decide(item.photoUrl, 'keep')}
                                                    >
                                                        Keep
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    disabled={actingOn === item.photoUrl}
                                                    onClick={() => decide(item.photoUrl, 'remove')}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default AdminPhotosScreen;
